import { z } from 'zod';

/*
 * Report builder: pick what to look at (dataset), how to split it (group by), what to measure,
 * and for which period. Everything is chosen from fixed lists, so reports are always safe and fast.
 */

export const REPORT_DATASETS = {
  leads: {
    label: 'Leads',
    groupBy: { owner: 'Salesperson', status: 'Status', stage: 'Stage', source: 'Source', campaign: 'Campaign', city: 'City', day: 'Day', week: 'Week', month: 'Month' },
    metrics: { count: 'Number of leads', won: 'Leads won', value: 'Expected value (₹)', wonValue: 'Won value (₹)' },
  },
  calls: {
    label: 'Calls',
    groupBy: { owner: 'Salesperson', direction: 'Call type', day: 'Day', week: 'Week', month: 'Month', hour: 'Hour of day' },
    metrics: { count: 'Number of calls', connected: 'Connected calls', talkMinutes: 'Talk time (minutes)', avgSeconds: 'Average call (seconds)' },
  },
  activities: {
    label: 'Work done on leads',
    groupBy: { owner: 'Salesperson', type: 'What was done', day: 'Day', week: 'Week', month: 'Month' },
    metrics: { count: 'Number of actions' },
  },
  documents: {
    label: 'Quotations & invoices',
    groupBy: { docType: 'Document type', status: 'Status', owner: 'Made by', customer: 'Customer', month: 'Month' },
    metrics: { count: 'Number', total: 'Total (₹)', paid: 'Received (₹)' },
  },
  payments: {
    label: 'Payments received',
    groupBy: { mode: 'Paid by', day: 'Day', week: 'Week', month: 'Month', customer: 'Customer' },
    metrics: { amount: 'Amount (₹)', count: 'Number of payments' },
  },
  attendance: {
    label: 'Attendance',
    groupBy: { owner: 'Person', day: 'Day', month: 'Month' },
    metrics: { present: 'Days present', late: 'Late check-ins', outside: 'Checked in outside office' },
  },
} as const;
export type ReportDataset = keyof typeof REPORT_DATASETS;

export const REPORT_PERIODS = {
  today: 'Today', yesterday: 'Yesterday', last7: 'Last 7 days', last30: 'Last 30 days', thisMonth: 'This month',
  lastMonth: 'Last month', thisYear: 'This year (Jan–Dec)', custom: 'Pick dates',
} as const;
export type ReportPeriod = keyof typeof REPORT_PERIODS;

export const reportDefinitionSchema = z
  .object({
    dataset: z.enum(Object.keys(REPORT_DATASETS) as [ReportDataset, ...ReportDataset[]]),
    groupBy: z.string().max(20),
    metric: z.string().max(20),
    period: z.enum(Object.keys(REPORT_PERIODS) as [ReportPeriod, ...ReportPeriod[]]).default('last30'),
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
    ownerIds: z.array(z.uuid()).max(100).default([]),
    sourceIds: z.array(z.uuid()).max(100).default([]),
    chart: z.enum(['table', 'bar', 'line']).default('bar'),
  })
  .refine((d) => d.groupBy in REPORT_DATASETS[d.dataset].groupBy, { error: 'Pick how to group this report.', path: ['groupBy'] })
  .refine((d) => d.metric in REPORT_DATASETS[d.dataset].metrics, { error: 'Pick what to measure.', path: ['metric'] })
  .refine((d) => d.period !== 'custom' || (!!d.from && !!d.to && d.from <= d.to), { error: 'Pick a start and end date.', path: ['to'] });
export type ReportDefinition = z.output<typeof reportDefinitionSchema>;

export const savedReportSchema = z.object({
  name: z.string().trim().min(1, { error: 'Give the report a name.' }).max(100),
  definition: reportDefinitionSchema,
  isShared: z.boolean().default(false),
  schedule: z.enum(['none', 'daily', 'weekly', 'monthly']).default('none'),
  sendHour: z.coerce.number().int().min(0).max(23).default(9),
  recipients: z.array(z.uuid()).max(50).default([]),
});
